import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert
} from '@mui/material';

const ROLE_OPTIONS = ['Client Account', 'System Admin'];

function normalizeRoleForForm(r) {
  const t = (r || '').trim();
  if (ROLE_OPTIONS.includes(t)) return t;
  if (t === 'Internal Admin' || t === 'Internal User') return 'System Admin';
  return 'Client Account';
}

const UserDialog = ({ open, onClose, onSave, user, saveError = '' }) => {
  const [formData, setFormData] = useState({
    identity_id: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'Client Account',
    password: '',
    newPassword: '',
    is_active: true,
    must_change_on_reset: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        identity_id: user.identity_id || user.id,
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: normalizeRoleForForm(user.role || (user.roles && user.roles[0])),
        password: '',
        newPassword: '',
        is_active: user.is_active !== false,
        must_change_on_reset: true
      });
    } else {
      setFormData({
        identity_id: '',
        email: '',
        first_name: '',
        last_name: '',
        role: 'Client Account',
        password: '',
        newPassword: '',
        is_active: true,
        must_change_on_reset: true
      });
    }
    setErrors({});
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.role) newErrors.role = 'Role is required';
    if (!user && !formData.password) newErrors.password = 'Password is required for new users';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {user ? 'Edit User' : 'New User'}
        </DialogTitle>
        <DialogContent>
          {saveError ? (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {saveError}
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!errors.email}
                helperText={errors.email}
                disabled={!!user}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                error={!!errors.first_name}
                helperText={errors.first_name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                error={!!errors.last_name}
                helperText={errors.last_name}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  label="Role"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!user && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Initial password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  error={!!errors.password}
                  helperText={errors.password || 'Min 12 chars; upper, lower, number, special.'}
                />
              </Grid>
            )}
            {user && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reset password (optional)
                  </Typography>
                  <TextField
                    fullWidth
                    type="password"
                    label="New password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    helperText="Leave blank to keep current password"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formData.must_change_on_reset}
                        onChange={(e) => setFormData({ ...formData, must_change_on_reset: e.target.checked })}
                      />
                    )}
                    label="User must change password on next login (when setting new password above)"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                )}
                label="Account active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog;
